/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { z } from "zod";

import { useAuthContext } from "../contexts/AuthContext";
import { cn } from "../utils/cn";

import { Button } from "./ui/Button";
import { CheckboxInput, FileInput, Input } from "./ui/Input";
import { Typography } from "./ui/Typography";

// Step indicator component
interface StepIndicatorProps {
	currentStep: number;
	steps: Array<{ number: number; title: string }>;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep }) => {
	return (
		<div className="flex flex-col gap-4 items-center justify-center w-full max-w-[604px]">
			{/* Progress line */}
			<div className="relative w-full h-[70px]">
				<div className="absolute left-[90px] top-[22px] w-[425px] h-0">
					<div className="absolute bottom-0 left-0 right-0 top-[-2px]">
						<svg width="425" height="2" viewBox="0 0 425 2" fill="none">
							<path d="M0 1H425" stroke="#E9E9E9" strokeWidth="2" />
						</svg>
					</div>
				</div>

				{/* Step 1 */}
				<div className="absolute left-0 top-0 w-[180px] flex flex-col gap-4 items-center">
					<div className={cn(
						"w-[43px] h-[43px] rounded-[50px] flex items-center justify-center",
						currentStep >= 1 ? "bg-blu-medio" : "bg-grigio"
					)}>
						<Typography
							variant="txt-18"
							color="bianco"
							className="font-normal"
						>
							1
						</Typography>
					</div>
					<Typography
						variant="txt-16"
						color={currentStep >= 1 ? "blu-medio" : "grigio"}
						className="text-center font-medium"
					>
						Anagrafica
					</Typography>
				</div>

				{/* Step 2 */}
				<div className="absolute left-[212px] top-0 w-[180px] flex flex-col gap-4 items-center">
					<div className={cn(
						"w-[43px] h-[43px] rounded-[50px] flex items-center justify-center",
						currentStep >= 2 ? "bg-blu-medio" : "bg-grigio"
					)}>
						<Typography
							variant="txt-18"
							color="bianco"
							className="font-normal"
						>
							2
						</Typography>
					</div>
					<Typography
						variant="txt-16"
						color={currentStep >= 2 ? "blu-medio" : "grigio"}
						className="text-center font-medium"
					>
						Dati azienda
					</Typography>
				</div>

				{/* Step 3 */}
				<div className="absolute left-[424px] top-0 w-[180px] flex flex-col gap-4 items-center">
					<div className={cn(
						"w-[43px] h-[43px] rounded-[50px] flex items-center justify-center",
						currentStep >= 3 ? "bg-blu-medio" : "bg-grigio"
					)}>
						<Typography
							variant="txt-18"
							color="bianco"
							className="font-normal"
						>
							3
						</Typography>
					</div>
					<Typography
						variant="txt-16"
						color={currentStep >= 3 ? "blu-medio" : "grigio"}
						className="text-center font-medium"
					>
						Domande guida
					</Typography>
				</div>
			</div>
		</div>
	);
};

// Validation schemas
const step1Schema = z.object({
	nome: z.string().min(1, "Nome è obbligatorio"),
	cognome: z.string().min(1, "Cognome è obbligatorio"),
	email: z.email("Email non valida"),
	telefono: z.string().min(1, "Telefono è obbligatorio"),
	password: z.string().min(8, "Password deve essere di almeno 8 caratteri"),
	confirmPassword: z.string().min(1, "Conferma password è obbligatoria"),
}).refine((data) => data.password === data.confirmPassword, {
	message: "Le password non coincidono",
	path: ["confirmPassword"],
});

const step2Schema = z.object({
	ragioneSociale: z.string().min(1, "Ragione sociale è obbligatoria"),
	partitaIva: z.string().min(1, "Partita IVA è obbligatoria"),
	codice_fiscale: z.string().min(1, "Codice fiscale è obbligatorio"),
	pec: z.email("PEC non valida"),
	codice_destinatario: z.string().optional(),
	sede_legale: z.string().optional(),
	cfPivaCoincidono: z.boolean().default(false),
}).refine((data) => {
	// Se CF e P.IVA coincidono, il codice fiscale deve essere uguale alla partita IVA
	if (data.cfPivaCoincidono) {
		return data.codice_fiscale === data.partitaIva;
	}
	return true;
}, {
	message: "Il codice fiscale deve coincidere con la partita IVA",
	path: ["codice_fiscale"],
});

const step3Schema = z.object({
	pagaProvvigioni: z.enum(["si", "no"]),
	esternalizzaFasi: z.enum(["si", "no"]),
	nonPossoAllegareBilancio: z.boolean().default(false),
});

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;
type Step3Data = z.infer<typeof step3Schema>;

interface RegistrationData {
	step1: Step1Data;
	step2: Step2Data;
	step3: Step3Data;
}

// Main component
export const RegistrationSteps: React.FC = () => {
	const router = useRouter();
	const { register, isLoading } = useAuthContext();
	const [currentStep, setCurrentStep] = useState(1);
	const [success, setSuccess] = useState(false);

	const [formData, setFormData] = useState<RegistrationData>({
		step1: {
			nome: "",
			cognome: "",
			email: "",
			telefono: "",
			password: "",
			confirmPassword: "",
		},
		step2: {
			ragioneSociale: "",
			partitaIva: "",
			codice_fiscale: "",
			pec: "",
			codice_destinatario: "",
			sede_legale: "",
			cfPivaCoincidono: false,
		},
		step3: {
			pagaProvvigioni: "si",
			esternalizzaFasi: "si",
			nonPossoAllegareBilancio: false,
		},
	});

	const [xbrlFile, setXbrlFile] = useState<File | null>(null);
	const [otherFormatFile, setOtherFormatFile] = useState<File | null>(null);
	const [fileUploadError, setFileUploadError] = useState<string>("");
	const [otherFormatError, setOtherFormatError] = useState<string>("");
	const [isUploading, setIsUploading] = useState(false);

	const [errors, setErrors] = useState<Record<string, string>>({});

	const updateFormData = (step: keyof RegistrationData, data: Partial<RegistrationData[keyof RegistrationData]>) => {
		setFormData(prev => ({
			...prev,
			[step]: { ...prev[step], ...data }
		}));
	};

	const handleFileSelect = (files: FileList | null) => {
		if (!files || files.length === 0) {
			setXbrlFile(null);
			setFileUploadError("");
			return;
		}

		const file = files[0];
		setFileUploadError("");

		// Validate file type
		const allowedTypes = [".xbrl", ".xml"];
		const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

		if (!allowedTypes.includes(fileExtension)) {
			setFileUploadError("Formato file non supportato. Utilizza solo file .xbrl o .xml");
			setXbrlFile(null);
			return;
		}

		// Validate file size (max 10MB)
		const maxSize = 10 * 1024 * 1024; // 10MB
		if (file.size > maxSize) {
			setFileUploadError("File troppo grande. Dimensione massima: 10MB");
			setXbrlFile(null);
			return;
		}

		setXbrlFile(file);
		setFileUploadError("");
	};

	const handleOtherFormatFileSelect = (files: FileList | null) => {
		if (!files || files.length === 0) {
			setOtherFormatFile(null);
			setOtherFormatError("");
			return;
		}

		const file = files[0];
		setOtherFormatError("");

		// Validate file size (max 10MB)
		const maxSize = 10 * 1024 * 1024; // 10MB
		if (file.size > maxSize) {
			setOtherFormatError("File troppo grande. Dimensione massima: 10MB");
			setOtherFormatFile(null);
			return;
		}

		setOtherFormatFile(file);
		setOtherFormatError("");
	};



	const validateStep = (step: number): boolean => {
		setErrors({});

		try {
			switch (step) {
				case 1:
					step1Schema.parse(formData.step1);
					break;
				case 2:
					// Se CF e P.IVA coincidono, assicurati che il codice fiscale sia uguale alla partita IVA
					if (formData.step2.cfPivaCoincidono) {
						formData.step2.codice_fiscale = formData.step2.partitaIva;
					}
					step2Schema.parse(formData.step2);
					break;
				case 3:
					step3Schema.parse(formData.step3);

					// Validate file requirement
					if (!formData.step3.nonPossoAllegareBilancio && !xbrlFile) {
						setErrors(prev => ({
							...prev,
							xbrlFile: "Devi caricare un file XBRL o selezionare \"Non posso allegare bilancio\""
						}));
						return false;
					}

					if (formData.step3.nonPossoAllegareBilancio && !otherFormatFile) {
						setErrors(prev => ({
							...prev,
							otherFormatFile: "Devi caricare un file in altro formato o deselezionare \"Non posso allegare bilancio\""
						}));
						return false;
					}

					// Clear file errors if files are uploaded
					if (xbrlFile) {
						setErrors(prev => {
							const { xbrlFile, ...rest } = prev;
							return rest;
						});
					}

					if (otherFormatFile) {
						setErrors(prev => {
							const { otherFormatFile, ...rest } = prev;
							return rest;
						});
					}
					break;
			}
			return true;
		} catch (error) {
			if (error instanceof z.ZodError) {
				const newErrors: Record<string, string> = {};
				error.issues.forEach((err: z.ZodIssue) => {
					if (err.path) {
						newErrors[err.path.join(".")] = err.message;
					}
				});
				setErrors(newErrors);
			}
			return false;
		}
	};

	const nextStep = () => {
		if (validateStep(currentStep)) {
			setCurrentStep(prev => Math.min(prev + 1, 3));
		}
	};

	const prevStep = () => {
		setCurrentStep(prev => Math.max(prev - 1, 1));
	};

	const handleSubmit = async () => {
		if (validateStep(3)) {
			try {
				setIsUploading(true);

				// Create FormData for registration with file upload
				const formDataToSend: FormData = new FormData();

				// Add basic registration data
				formDataToSend.append("nome", formData.step1.nome);
				formDataToSend.append("cognome", formData.step1.cognome);
				formDataToSend.append("email", formData.step1.email);
				formDataToSend.append("telefono", formData.step1.telefono);
				formDataToSend.append("ragione_sociale", formData.step2.ragioneSociale);
				formDataToSend.append("partita_iva", formData.step2.partitaIva);
				formDataToSend.append("codice_fiscale", formData.step2.cfPivaCoincidono ? formData.step2.partitaIva : formData.step2.codice_fiscale);
				formDataToSend.append("pec", formData.step2.pec);
				formDataToSend.append("paga_provvigioni", (formData.step3.pagaProvvigioni === "si").toString());
				formDataToSend.append("esternalizza_fasi", (formData.step3.esternalizzaFasi === "si").toString());
				formDataToSend.append("password", formData.step1.password);

				// Add XBRL file if provided
				if (xbrlFile && !formData.step3.nonPossoAllegareBilancio) {
					formDataToSend.append("xbrl_file", xbrlFile);
					formDataToSend.append("xbrl_file_name", xbrlFile.name);
				}

				// Add other format file if provided
				if (otherFormatFile && formData.step3.nonPossoAllegareBilancio) {
					formDataToSend.append("other_format_file", otherFormatFile);
					formDataToSend.append("other_format_file_name", otherFormatFile.name);
				}

				// Add optional fields
				if (formData.step2.codice_destinatario) {
					formDataToSend.append("codice_destinatario", formData.step2.codice_destinatario);
				}
				if (formData.step2.sede_legale) {
					formDataToSend.append("sede_legale", formData.step2.sede_legale);
				}

				// Call register with FormData
				await register(formDataToSend);
				setSuccess(true);
				setTimeout(() => {
					router.push("/login");
				}, 5000);
			} finally {
				setIsUploading(false);
			}
		}
	};

	if (success) {
		return (
			<div className="flex flex-col gap-20 items-center justify-start w-full max-w-[816px]">
				<div className="flex flex-col gap-8 items-center text-center w-[562px]">
					<Typography variant="h2" color="blu-medio">
						Registrazione completata!
					</Typography>
					<Typography variant="txt-18" color="blu-scuro">
						Ti abbiamo inviato un&apos;email di conferma. Controlla la tua casella di posta e clicca sul link per verificare il tuo account.
					</Typography>
					<Typography variant="txt-16" color="blu-scuro">
						Reindirizzamento alla pagina di login...
					</Typography>
				</div>
			</div>
		);
	}

	const renderStep1 = () => (
		<div className="flex flex-col gap-6 items-start justify-start w-full">
			<div className="flex gap-8 items-center justify-start w-full">
				<Input
					label="Nome"
					placeholder="Nome"
					value={formData.step1.nome}
					onValueChange={(value) => updateFormData("step1", { nome: value })}
					errorText={errors["nome"]}
					showError={!!errors["nome"]}
					fullWidth
				/>
				<Input
					label="Cognome"
					placeholder="Cognome"
					value={formData.step1.cognome}
					onValueChange={(value) => updateFormData("step1", { cognome: value })}
					errorText={errors["cognome"]}
					showError={!!errors["cognome"]}
					fullWidth
				/>
			</div>

			<div className="flex gap-8 items-center justify-start w-full">
				<Input
					label="Indirizzo email"
					type="email"
					placeholder="Indirizzo email"
					value={formData.step1.email}
					onValueChange={(value) => updateFormData("step1", { email: value })}
					errorText={errors["email"]}
					showError={!!errors["email"]}
					fullWidth
				/>
				<Input
					label="Telefono"
					type="tel"
					placeholder="Telefono"
					value={formData.step1.telefono}
					onValueChange={(value) => updateFormData("step1", { telefono: value })}
					errorText={errors["telefono"]}
					showError={!!errors["telefono"]}
					fullWidth
				/>
			</div>

			<div className="flex gap-8 items-center justify-start w-full">
				<Input
					label="Password"
					type="password"
					placeholder="Password"
					value={formData.step1.password}
					onValueChange={(value) => updateFormData("step1", { password: value })}
					errorText={errors["password"]}
					showError={!!errors["password"]}
					fullWidth
				/>
				<Input
					label="Conferma password"
					type="password"
					placeholder="Conferma password"
					value={formData.step1.confirmPassword}
					onValueChange={(value) => updateFormData("step1", { confirmPassword: value })}
					errorText={errors["confirmPassword"]}
					showError={!!errors["confirmPassword"]}
					fullWidth
				/>
			</div>
		</div>
	);

	const renderStep2 = () => (
		<div className="flex flex-col gap-6 items-start justify-start w-full">
			<div className="flex gap-8 items-start justify-start w-full">
				<Input
					label="Ragione sociale*"
					placeholder="Ragione sociale"
					value={formData.step2.ragioneSociale}
					onValueChange={(value) => updateFormData("step2", { ragioneSociale: value })}
					errorText={errors["ragioneSociale"]}
					showError={!!errors["ragioneSociale"]}
					fullWidth
				/>
				<div className="flex flex-col gap-4 items-start justify-start w-full">
					<Input
						label="Partita IVA*"
						placeholder="Partita IVA"
						value={formData.step2.partitaIva}
						onValueChange={(value) => {
							updateFormData("step2", { partitaIva: value });
							// Se CF e P.IVA coincidono, aggiorna anche il codice fiscale
							if (formData.step2.cfPivaCoincidono) {
								updateFormData("step2", { codice_fiscale: value });
							}
						}}
						errorText={errors["partitaIva"]}
						showError={!!errors["partitaIva"]}
						fullWidth
					/>
					<CheckboxInput
						label="CF e P.IVA coincidono"
						checked={formData.step2.cfPivaCoincidono}
						onChange={(checked: boolean) => updateFormData("step2", { cfPivaCoincidono: checked })}
					/>
				</div>
			</div>

			<div className="flex gap-8 items-start justify-start w-full">
				{!formData.step2.cfPivaCoincidono && (
					<Input
						label="Codice fiscale*"
						placeholder="Codice fiscale"
						value={formData.step2.codice_fiscale}
						onValueChange={(value) => updateFormData("step2", { codice_fiscale: value })}
						errorText={errors["codice_fiscale"]}
						showError={!!errors["codice_fiscale"]}
						fullWidth
					/>
				)}
				<Input
					label="Codice destinatario (facoltativo)"
					placeholder="Codice destinatario (facoltativo)"
					value={formData.step2.codice_destinatario}
					onValueChange={(value) => updateFormData("step2", { codice_destinatario: value })}
					fullWidth
				/>
			</div>

			<div className="flex gap-8 items-start justify-start w-full">
				{/* todo: aggiungere input per ogni sede operativa */}
				<Input
					label="PEC*"
					type="email"
					placeholder="PEC"
					value={formData.step2.pec}
					onValueChange={(value) => updateFormData("step2", { pec: value })}
					errorText={errors["pec"]}
					showError={!!errors["pec"]}
					fullWidth
				/>
				{/* todo: aggiungere input per sede legale con UserAddress */}
				<Input
					label="Sede legale (facoltativo)"
					placeholder="Sede legale (facoltativo)"
					value={formData.step2.sede_legale}
					onValueChange={(value) => updateFormData("step2", { sede_legale: value })}
					fullWidth
				/>
			</div>
		</div>
	);

	const renderStep3 = () => (
		<div className="flex flex-col gap-16 items-end justify-start w-full">
			<div className="flex flex-col gap-12 items-start justify-start w-full max-w-[796px]">
				<div className="flex items-center justify-between w-full">
					<Typography variant="txt-18" color="blu-scuro" className="text-left font-normal">
						Paghi provvigioni a segnalatori, agenti, venditori o procacciatori?
					</Typography>
					<div className="flex gap-6 items-center justify-start">
						<CheckboxInput
							label="Sì"
							checked={formData.step3.pagaProvvigioni === "si"}
							onChange={() => updateFormData("step3", { pagaProvvigioni: "si" })}
						/>

						<CheckboxInput
							label="No"
							checked={formData.step3.pagaProvvigioni === "no"}
							onChange={() => updateFormData("step3", { pagaProvvigioni: "no" })}
						/>
					</div>
				</div>

				<div className="flex gap-[85px] items-center justify-between w-full">
					<Typography variant="txt-16" color="blu-scuro" className="text-center font-medium">
						Esternalizzi fasi produttive o esecutive (contoterzi, subforniture, outsourcing)?
					</Typography>
					<div className="flex gap-6 items-center justify-start">
						<CheckboxInput
							label="Sì"
							checked={formData.step3.esternalizzaFasi === "si"}
							onChange={() => updateFormData("step3", { esternalizzaFasi: "si" })}
						/>

						<CheckboxInput
							label="No"
							checked={formData.step3.esternalizzaFasi === "no"}
							onChange={() => updateFormData("step3", { esternalizzaFasi: "no" })}
						/>
					</div>
				</div>
			</div>

			<div className="flex gap-[47px] items-start justify-start w-full">
				<div className="w-[392px]">
					{/* XBRL File Input - Disabled when "Non posso allegare bilancio" is checked */}
					<FileInput
						label="Upload bilancio in formato XBRL"
						placeholder="Carica bilancio in formato xbrl o xml"
						accept=".xbrl,.xml"
						onFileSelect={handleFileSelect}
						errorText={fileUploadError || errors["xbrlFile"]}
						showError={!!fileUploadError || !!errors["xbrlFile"]}
						state={(fileUploadError || errors["xbrlFile"]) ? "error" : "default"}
						disabled={formData.step3.nonPossoAllegareBilancio}
					/>

					{/* Display selected XBRL file info */}
					{xbrlFile && !formData.step3.nonPossoAllegareBilancio && (
						<div className="mt-3 p-3 bg-grigio/10 rounded-lg border border-grigio">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 bg-verde rounded-full flex items-center justify-center">
										<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
											<path d="M6.5 10.5L2.5 6.5L3.91 5.09L6.5 7.68L12.09 2.09L13.5 3.5L6.5 10.5Z" fill="white" />
										</svg>
									</div>
									<span className="text-sm text-blu-scuro font-medium">{xbrlFile.name}</span>
								</div>
								<button
									type="button"
									onClick={() => setXbrlFile(null)}
									className="text-rosso hover:text-rosso/80 transition-colors"
								>
									<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
										<path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</button>
							</div>
							<div className="mt-1 text-xs text-blu-chiaro">
								{(xbrlFile.size / 1024).toFixed(2)} KB
							</div>
						</div>
					)}

					{/* Other Format File Input - Only shown when "Non posso allegare bilancio" is checked */}
					{formData.step3.nonPossoAllegareBilancio && (
						<>
							<FileInput
								label="Carica bilancio in altri formati"
								placeholder="Carica file in formato pdf, doc, excel..."
								accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
								onFileSelect={handleOtherFormatFileSelect}
								errorText={otherFormatError || errors["otherFormatFile"]}
								showError={!!otherFormatError || !!errors["otherFormatFile"]}
								state={(otherFormatError || errors["otherFormatFile"]) ? "error" : "default"}
								className="mt-4"
							/>

							{/* Display selected other format file info */}
							{otherFormatFile && (
								<div className="mt-3 p-3 bg-grigio/10 rounded-lg border border-grigio">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											<div className="w-4 h-4 bg-verde rounded-full flex items-center justify-center">
												<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
													<path d="M6.5 10.5L2.5 6.5L3.91 5.09L6.5 7.68L12.09 2.09L13.5 3.5L6.5 10.5Z" fill="white" />
												</svg>
											</div>
											<span className="text-sm text-blu-scuro font-medium">{otherFormatFile.name}</span>
										</div>
										<button
											type="button"
											onClick={() => setOtherFormatFile(null)}
											className="text-rosso hover:text-rosso/80 transition-colors"
										>
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
												<path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
											</svg>
										</button>
									</div>
									<div className="mt-1 text-xs text-blu-chiaro">
										{(otherFormatFile.size / 1024).toFixed(2)} KB
									</div>
								</div>
							)}
						</>
					)}
				</div>
				<div className="h-[46px] relative w-[357px]">
					<CheckboxInput
						label="Non posso allegare bilancio in formato xbrl"
						checked={formData.step3.nonPossoAllegareBilancio}
						onChange={() => {
							const newValue = !formData.step3.nonPossoAllegareBilancio;
							updateFormData("step3", { nonPossoAllegareBilancio: newValue });

							// Clear files when switching between modes
							if (newValue) {
								// User checked "Non posso allegare bilancio" - clear XBRL file
								setXbrlFile(null);
								setFileUploadError("");
							} else {
								// User unchecked - clear other format file
								setOtherFormatFile(null);
								setOtherFormatError("");
							}
						}}
					/>
				</div>
			</div>
		</div>
	);

	const renderCurrentStep = () => {
		switch (currentStep) {
			case 1:
				return renderStep1();
			case 2:
				return renderStep2();
			case 3:
				return renderStep3();
			default:
				return null;
		}
	};

	const renderNavigationButtons = () => {
		if (currentStep === 1) {
			return (
				<div className="w-full flex justify-end">
					<Button onClick={nextStep} variant="primary" buttonSize="lg">
						Prossimo
					</Button>
				</div>
			);
		} else if (currentStep === 2) {
			return (
				<div className="w-full flex justify-end gap-3">
					<Button onClick={prevStep} variant="secondary" buttonSize="lg">
						Precedente
					</Button>
					<Button onClick={nextStep} variant="primary" buttonSize="lg">
						Prossimo
					</Button>
				</div>
			);
		} else {
			return (
				<div className="w-full flex justify-end gap-3">
					<Button onClick={prevStep} variant="secondary" disabled={isLoading} buttonSize="lg">
						Precedente
					</Button>
					<Button
						onClick={handleSubmit}
						variant="primary"
						buttonSize="lg"
						state={(isLoading || isUploading) ? "loading" : "default"}
						loadingText={isUploading ? "Caricamento file..." : "Creazione account..."}
					>
						Crea account
					</Button>
				</div>
			);
		}
	};

	return (
		<div className="flex flex-col gap-20 items-center justify-start w-full max-w-[816px]">
			{/* Header */}
			<div className="flex flex-col gap-8 items-center justify-start text-center w-[562px]">
				<Typography variant="h2" color="blu-medio" className="font-bold">
					Inizia da qui
				</Typography>
				<Typography variant="txt-18" color="blu-scuro" className="font-normal">
					Completa la registrazione in meno di un minuto.
				</Typography>
			</div>

			{/* Steps */}
			<div className="flex flex-col gap-16 items-center justify-start w-full">
				<StepIndicator currentStep={currentStep} steps={[
					{ number: 1, title: "Anagrafica" },
					{ number: 2, title: "Dati azienda" },
					{ number: 3, title: "Domande guida" }
				]} />

				{/* Form content */}
				<div className="w-full">
					{renderCurrentStep()}
				</div>

				{/* Navigation buttons */}
				{renderNavigationButtons()}
			</div>

			{/* Login Link */}
			<div className="text-center">
				<Typography variant="txt-16" color="blu-scuro">
					Hai già un account?{" "}
					<Link href="/login">
						<span className="text-blu-medio hover:text-blu-chiaro transition-colors cursor-pointer">
							Accedi
						</span>
					</Link>
				</Typography>
			</div>
		</div>
	);
};
